import {Box, SvgIcon, Toolbar, Typography} from '@mui/material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import {BusinessOutlined} from '@mui/icons-material';
import {ActiveBusinessSelect} from '../ActiveBusinessSelect';
import {useCan, useCanOpen, usePersona, useScope} from 'src/permissions/hooks';
import {PAGE_PERMISSION} from 'src/permissions/pages';
import {FC, useMemo} from 'react';
import {FormattedMessage} from 'react-intl';
import {NavLink, useLocation} from 'react-router-dom';
import {AppRouteEnum, AppRouteSpace} from 'src/utils/helper';
import {
    AssistantIcon,
    ChargersIcon,
    ContractsIcon,
    DashboardIcon,
    EmployeesIcon,
    InvoicesIcon,
    LocationsIcon,
    NotificationsIcon,
    ReportsIcon,
    TagsIcon,
    TariffsIcon,
    TechnicalTeamIcon,
    UsersIcon,
} from 'src/components/icons/ToolbarIcons';
import {linkSx, prominentLinkSx, toolbarSx} from './MainNavigation.sx';
import {useBusinessId, useAppSelector} from 'src/app/hooks';
import {ContractStatusEnum} from 'src/enums';
import {AuthContractDTO} from 'src/types';
import {NotificationMarker} from '../NotificationMarker';
import {MapsHomeWorkSharp} from '@mui/icons-material';

/**
 * The sidebar, for everybody.
 *
 * There used to be two of these and a rule that chose between them, and the rule was wrong three times
 * running: first `manage all`, which every old role had; then userType, which disagrees with the roles
 * a person holds; then reach, which put an employee in the driver frame because their scope says SELF.
 * Each fix moved the bug rather than removing it, because the frame decided what a permission could
 * ever surface: the driver frame simply had no RFID entry, so no grant could produce one.
 *
 * One menu, and every entry asks what its own page requires. A driver holding nothing but driver
 * permissions sees what they saw; give them a role and its pages appear. Nothing has to know who they
 * are.
 *
 * The three personal entries are the exception, and they are gated by data rather than by persona: a
 * link to *your* charger only means something if you have one, and it stands in for the list when you
 * cannot open the list.
 */
export const MainNavigation: FC = () => {
    // The business being worked in: the caller's own, or the one a platform administrator picked.
    // Reading `authInfo.business.id` directly was a crash for them - they have no business of
    // their own, and null has no id.
    const businessId = useBusinessId();
    const location = useLocation();
    const canReadBusiness = useCan('businesses.read');
    // Reading one charger, as opposed to listing them: what a driver holds for the charger that is
    // theirs.
    const canReadChargers = useCan('chargers.read');
    const persona = usePersona();
    // Which staff directory somebody gets follows their reach, not the kind of account they are.
    // A company reaches a company, and its people are Employees; anyone reaching the business gets
    // Users and the technical team. Asking userType instead gave an employee holding the company role
    // the business directories and hid the one their role was for - the two mistakes were the same
    // check read in opposite directions.
    const companyReach = useScope()?.type === 'COMPANY';
    // Users and the technical team are the business's own staff directories. Both are reached with
    // `users.search`, which a company holds for its own slice, so the permission alone keeps
    // them on screen — but neither list has ever been theirs to browse. Their directory is Employees.
    const showsBusinessDirectory = !companyReach;
    // One call per entry, in a fixed order — each asking exactly what its page requires. Anything
    // else and the menu offers a door the router will not open.
    const can = {
        locations: useCanOpen(PAGE_PERMISSION[AppRouteEnum.locations]),
        chargers: useCanOpen(PAGE_PERMISSION[AppRouteEnum.chargers]),
        employees: useCanOpen(PAGE_PERMISSION[AppRouteEnum.employee]),
        users: useCanOpen(PAGE_PERMISSION[AppRouteEnum.users]),
        rfid: useCanOpen(PAGE_PERMISSION[AppRouteEnum.rfid]),
        contracts: useCanOpen(PAGE_PERMISSION[AppRouteEnum.contracts]),
        tariffs: useCanOpen(PAGE_PERMISSION[AppRouteEnum.tariffs]),
        reports: useCanOpen(PAGE_PERMISSION[AppRouteEnum.reports]),
        invoices: useCanOpen(PAGE_PERMISSION[AppRouteEnum.invoices]),
        technicalTeam: useCanOpen(PAGE_PERMISSION[AppRouteEnum.technicalTeam]),
        notifications: useCanOpen(PAGE_PERMISSION[AppRouteEnum.notifications]),
        reOwner: useCanOpen(PAGE_PERMISSION[AppRouteEnum.reOwner]),
        accessControl: useCanOpen(PAGE_PERMISSION[AppRouteEnum.accessControl]),
        businesses: useCanOpen(PAGE_PERMISSION[AppRouteEnum.businesses]),
        assistant: useCanOpen(PAGE_PERMISSION[AppRouteEnum.assistant]),
    };
    const {authInfo} = useAppSelector((state) => state.common);
    const availableContract = authInfo.contracts?.find(
        (contract: AuthContractDTO) =>
            contract.status === ContractStatusEnum.WAIT_FOR_SIGNATURE ||
            contract?.status === ContractStatusEnum.OPERATION ||
            contract?.status === ContractStatusEnum.INSTALLATION
    );
    const operationalContract = authInfo.contracts?.find(
        (contract: AuthContractDTO) => contract?.status === ContractStatusEnum.OPERATION
    );
    const isBusinessPage = useMemo(() => {
        return (
            location.pathname ===
            AppRouteSpace([
                AppRouteEnum.app,
                AppRouteEnum.users,
                AppRouteEnum.business,
                String(businessId),
            ])
        );
    }, [location]);


    return (
        <Toolbar sx={toolbarSx}>
            {/* The assistant is a separate service, but who may open it is this service's decision and
                now says so: agent.use is a catalog entry like any other. It replaced a boolean on the
                account, which no permission could contradict and no role could grant. */}
            {can.assistant && (
                <Box sx={prominentLinkSx()}>
                    <NavLink to={AppRouteSpace([AppRouteEnum.app, AppRouteEnum.assistant])}>
                        <SvgIcon
                            component={AssistantIcon}
                            viewBox="0 0 20 20"
                        />
                        <Box className="ai-chip">AI</Box>
                        <Typography variant="subtitle1" noWrap>
                            Assistant
                        </Typography>
                    </NavLink>
                </Box>
            )}

            {/* Directly under the assistant and above everything else, because it governs everything
                else: every screen below reads the business it is set to. */}
            <ActiveBusinessSelect/>

            {can.businesses && (
                <Box sx={linkSx()}>
                    <NavLink to={AppRouteSpace([AppRouteEnum.app, AppRouteEnum.businesses])}>
                        <SvgIcon
                            component={BusinessOutlined}
                            viewBox="0 0 24 24"
                        />
                        <Typography variant="subtitle1">
                            <FormattedMessage id="businesses"/>
                        </Typography>
                    </NavLink>
                </Box>
            )}

            {/* The permission and the business itself. This entry links to the caller's own business,
                and the platform administrator holds businesses.read while having none - so the
                permission alone let the link build a path from `null.id` and took the whole navigation
                down with it. A guard has to cover the data it dereferences, not only the right to it. */}
            {canReadBusiness && businessId && (
                <Box sx={linkSx()}>
                    <NavLink
                        to={AppRouteSpace([
                            AppRouteEnum.app,
                            AppRouteEnum.users,
                            AppRouteEnum.business,
                            String(businessId),
                        ])}
                    >
                        <SvgIcon
                            component={DashboardIcon}
                            viewBox="0 0 20 20"
                        />
                        <Typography variant="subtitle1">
                            <FormattedMessage id="dashboard"/>
                        </Typography>
                    </NavLink>
                </Box>
            )}
            {can.locations && (
                <Box sx={linkSx()}>
                    <NavLink to={AppRouteSpace([AppRouteEnum.app, AppRouteEnum.locations])}>
                        <SvgIcon
                            component={LocationsIcon}
                            viewBox="0 0 20 20"
                        />
                        <Typography variant="subtitle1">
                            <FormattedMessage id="locations"/>
                        </Typography>
                    </NavLink>
                </Box>
            )}
            {can.chargers && (
                <Box sx={linkSx()}>
                    <NavLink to={AppRouteSpace([AppRouteEnum.app, AppRouteEnum.chargers])}>
                        <SvgIcon
                            component={ChargersIcon}
                            viewBox="0 0 20 20"
                        />
                        <Typography variant="subtitle1">
                            <FormattedMessage id="chargers"/>
                        </Typography>
                    </NavLink>
                </Box>
            )}
            {/* Your own charger, when you cannot open the list. A driver holds chargers.read but not
                chargers.search, so the entry above stays hidden and this one takes its place. Both at
                once would be two doors to the same room. */}
            {!can.chargers && canReadChargers && authInfo.chargePoint && availableContract && (
                <Box sx={linkSx()}>
                    <NavLink
                        to={AppRouteSpace([
                            AppRouteEnum.app,
                            AppRouteEnum.chargers,
                            authInfo.chargePoint.id,
                        ])}
                    >
                        <SvgIcon
                            component={ChargersIcon}
                            viewBox="0 0 20 20"
                        />
                        <Typography variant="subtitle1">
                            <FormattedMessage id="charger"/>
                        </Typography>
                    </NavLink>
                </Box>
            )}
            {/* The contract flow, which redirects anyone who is neither a driver nor a company
                straight to Locations - so the entry asks the same question the page asks about
                itself. */}
            {(persona.isDriver || persona.isCompany) && !operationalContract && (
                <Box sx={linkSx()}>
                    <NavLink to={AppRouteSpace([AppRouteEnum.app, AppRouteEnum.welcome])}>
                        <SvgIcon
                            component={MapsHomeWorkSharp}
                            viewBox="0 0 20 20"
                        />
                        <Typography variant="subtitle1">
                            <FormattedMessage id="home"/>
                        </Typography>
                    </NavLink>
                </Box>
            )}
            {!can.contracts && availableContract && (
                <Box sx={linkSx()}>
                    <NavLink
                        to={AppRouteSpace([
                            AppRouteEnum.app,
                            AppRouteEnum.contracts,
                            String(operationalContract?.id ?? availableContract.id),
                        ])}
                    >
                        <SvgIcon
                            component={ContractsIcon}
                            viewBox="0 0 20 20"
                        />
                        <Typography variant="subtitle1">
                            <FormattedMessage id="contracts"/>
                        </Typography>
                    </NavLink>
                </Box>
            )}
            {/* A company's own staff list. The business owner can read employees through the
                API and does so from the users table, but this entry has always been the company's -
                showing it to the business is a navigation change nobody asked for. Permission and
                persona both have to agree: the first is authorization, the second is whose menu it is. */}
            {companyReach && can.employees && (
                <Box sx={linkSx()}>
                    <NavLink to={AppRouteSpace([AppRouteEnum.app, AppRouteEnum.employee])}>
                        <SvgIcon
                            component={EmployeesIcon}
                            viewBox="0 0 20 20"
                        />
                        <Typography variant="subtitle1">
                            <FormattedMessage id="employee"/>
                        </Typography>
                    </NavLink>
                </Box>
            )}
            {showsBusinessDirectory && can.users && (
                <Box sx={linkSx(isBusinessPage)}>
                    <NavLink to={AppRouteSpace([AppRouteEnum.app, AppRouteEnum.users])}>
                        <SvgIcon
                            component={UsersIcon}
                            viewBox="0 0 20 20"
                        />
                        <Typography variant="subtitle1">
                            <FormattedMessage id="users"/>
                        </Typography>
                    </NavLink>
                </Box>
            )}
            {can.rfid && (
                <Box sx={linkSx()}>
                    <NavLink to={AppRouteSpace([AppRouteEnum.app, AppRouteEnum.rfid])}>
                        <SvgIcon
                            component={TagsIcon}
                            viewBox="0 0 24 22"
                        />
                        <Typography variant="subtitle1">
                            <FormattedMessage id="rfidsTabHeader"/>
                        </Typography>
                    </NavLink>
                </Box>
            )}
            {can.contracts && (
                <Box sx={linkSx()}>
                    <NavLink to={AppRouteSpace([AppRouteEnum.app, AppRouteEnum.contracts])}>
                        <SvgIcon
                            component={ContractsIcon}
                            viewBox="0 0 20 20"
                        />
                        <Typography variant="subtitle1">
                            <FormattedMessage id="contracts"/>
                        </Typography>
                    </NavLink>
                </Box>
            )}
            {can.tariffs && (
                <Box sx={linkSx()}>
                    <NavLink to={AppRouteSpace([AppRouteEnum.app, AppRouteEnum.tariffs])}>
                        <SvgIcon
                            component={TariffsIcon}
                            viewBox="0 0 20 20"
                        />
                        <Typography variant="subtitle1">
                            <FormattedMessage id="tariffs"/>
                        </Typography>
                    </NavLink>
                </Box>
            )}
            {can.reports && (
                <Box sx={linkSx()}>
                    <NavLink to={AppRouteSpace([AppRouteEnum.app, AppRouteEnum.reports])}>
                        <SvgIcon
                            component={ReportsIcon}
                            viewBox="0 0 20 20"
                        />
                        <Typography variant="subtitle1">
                            <FormattedMessage id="statistics"/>
                        </Typography>
                    </NavLink>
                </Box>
            )}
            {can.invoices && (
                <Box sx={linkSx()}>
                    <NavLink to={AppRouteSpace([AppRouteEnum.app, AppRouteEnum.invoices])}>
                        <SvgIcon
                            component={InvoicesIcon}
                            viewBox="0 0 20 20"
                        />
                        <Typography variant="subtitle1">
                            <FormattedMessage id="invoices"/>
                        </Typography>
                    </NavLink>
                </Box>
            )}
            {showsBusinessDirectory && can.technicalTeam && (
                <Box sx={linkSx()}>
                    <NavLink to={AppRouteSpace([AppRouteEnum.app, AppRouteEnum.technicalTeam])}>
                        <SvgIcon
                            component={TechnicalTeamIcon}
                            viewBox="0 0 20 20"
                        />
                        <Typography variant="subtitle1">
                            <FormattedMessage id="technical-team"/>
                        </Typography>
                    </NavLink>
                </Box>
            )}
            {can.notifications && (
                <Box sx={linkSx()}>
                    <NavLink to={AppRouteSpace([AppRouteEnum.app, AppRouteEnum.notifications])}>
                        <SvgIcon
                            component={NotificationsIcon}
                            viewBox="0 0 20 20"
                        />
                        <Typography variant="subtitle1">
                            <FormattedMessage id="notifications"/>
                        </Typography>
                        <NotificationMarker/>
                    </NavLink>
                </Box>
            )}
            {can.accessControl && (
                <Box sx={linkSx()}>
                    <NavLink to={AppRouteSpace([AppRouteEnum.app, AppRouteEnum.accessControl])}>
                        <SvgIcon
                            component={AdminPanelSettingsIcon}
                            viewBox="0 0 24 24"
                        />
                        <Typography variant="subtitle1">
                            <FormattedMessage id="accessControl"/>
                        </Typography>
                    </NavLink>
                </Box>
            )}
            {can.reOwner && (
                <Box sx={linkSx()}>
                    <NavLink to={AppRouteSpace([AppRouteEnum.app, AppRouteEnum.reOwner])}>
                        <SvgIcon
                            component={MapsHomeWorkSharp}
                            viewBox="0 0 20 20"
                        />
                        <Typography variant="subtitle1">
                            <FormattedMessage id="reOwner"/>
                        </Typography>
                    </NavLink>
                </Box>
            )}
        </Toolbar>
    );
};
